import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    AlertTriangle,
    BarChart3,
    Building2,
    Calendar,
    Hash,
    Info,
    Map as MapIcon,
    MapPin,
    Search
} from 'lucide-react';
import ParkingLotMap, { ParkingLotMapOverlayMarker } from '../../components/ParkingLotMap';
import LoadingState from '../../components/common/LoadingState';
import ErrorState from '../../components/common/ErrorState';
import { getParkingLotById, useActionHeatmapData, useCities, useParkingLotsMapView } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import {
    ActionHeatmapAction,
    ActionHeatmapDisplayMode,
    ActionHeatmapSearchBucket,
    ParkingLot
} from '../../lib/types';
import { getGeometryLabelPosition, normalizeMapItemToParkingLot } from '../../lib/utils';

const DEFAULT_CITY = 'aalborg';
const DEFAULT_ACTION: ActionHeatmapAction = 'navigation_started';
const DEFAULT_DISPLAY_MODE: ActionHeatmapDisplayMode = 'intensity';

const ACTION_OPTIONS: Array<{ value: ActionHeatmapAction; label: string; description: string }> = [
    {
        value: 'search_performed',
        label: 'Search performed',
        description: 'Shows marker buckets because searches are not tied to individual lot polygons.'
    },
    {
        value: 'navigation_started',
        label: 'Navigation started',
        description: 'Counts when users start navigation to a lot.'
    },
    {
        value: 'parking_lot_viewed',
        label: 'Parking lot viewed',
        description: 'Counts parking-lot detail views captured in the DB.'
    },
    {
        value: 'payment_successful',
        label: 'Payment successful',
        description: 'Counts charged parking sessions per lot.'
    },
    {
        value: 'feedback_submitted',
        label: 'Feedback submitted',
        description: 'Counts feedback events attached to each lot.'
    },
    {
        value: 'address_copied',
        label: 'Address copied',
        description: 'Counts address-copy interactions captured in the DB.'
    },
    {
        value: 'spot_shared',
        label: 'Spot shared',
        description: 'Counts parking-spot share events.'
    },
    {
        value: 'favorites_current',
        label: 'Favorites',
        description: 'Shows the current number of saved favorites per lot. Date filters do not apply to this snapshot.'
    }
];

const DISPLAY_MODE_OPTIONS: Array<{ value: ActionHeatmapDisplayMode; label: string }> = [
    { value: 'intensity', label: 'Heat intensity' },
    { value: 'count', label: 'Polygon count labels' }
];

type SelectedHeatmapItem =
    | {
        type: 'lot';
        lotId: string;
        count: number;
    }
    | {
        type: 'search';
        bucketId: string;
        bucket: ActionHeatmapSearchBucket;
    };

const formatDateInputValue = (date: Date): string => {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const shiftDateByDays = (date: Date, days: number): Date => {
    const nextDate = new Date(date);
    nextDate.setDate(nextDate.getDate() + days);
    return nextDate;
};

const formatCompactCount = (value: number): string => {
    if (value > 999) {
        return `${Math.round(value / 100) / 10}k`;
    }
    return value.toLocaleString();
};

const formatMarkerCount = (value: number): string => {
    if (value > 999) {
        return '999+';
    }
    return value.toLocaleString();
};

const getPolygonStyleForCount = (count: number, maxCount: number): Partial<google.maps.PolygonOptions> => {
    if (count <= 0 || maxCount <= 0) {
        return {
            fillColor: '#d1d5db',
            fillOpacity: 0.2,
            strokeColor: '#9ca3af',
            strokeWeight: 2
        };
    }

    const intensityPalette = [
        { fillColor: '#dbeafe', fillOpacity: 0.32, strokeColor: '#93c5fd' },
        { fillColor: '#bfdbfe', fillOpacity: 0.4, strokeColor: '#60a5fa' },
        { fillColor: '#93c5fd', fillOpacity: 0.5, strokeColor: '#3b82f6' },
        { fillColor: '#60a5fa', fillOpacity: 0.6, strokeColor: '#2563eb' },
        { fillColor: '#2563eb', fillOpacity: 0.72, strokeColor: '#1d4ed8' }
    ];

    const normalized = maxCount > 0 ? count / maxCount : 0;
    const bucketIndex = Math.max(
        0,
        Math.min(intensityPalette.length - 1, Math.ceil(normalized * intensityPalette.length) - 1)
    );
    const paletteEntry = intensityPalette[bucketIndex];

    return {
        fillColor: paletteEntry.fillColor,
        fillOpacity: paletteEntry.fillOpacity,
        strokeColor: paletteEntry.strokeColor,
        strokeWeight: 2.5
    };
};

const getSearchMarkerScale = (count: number, maxCount: number): number => {
    if (maxCount <= 0) {
        return 18;
    }

    const normalized = count / maxCount;
    return 16 + Math.round(normalized * 10);
};

const summaryCards = [
    {
        key: 'totalMatchedEvents',
        label: 'Total matched events',
        icon: BarChart3
    },
    {
        key: 'matchedLots',
        label: 'Matched lots',
        icon: Building2
    },
    {
        key: 'maxCount',
        label: 'Max count on one lot',
        icon: Hash
    },
    {
        key: 'unmatchedCount',
        label: 'Unmatched records',
        icon: AlertTriangle
    }
] as const;

const ActionHeatmapPage: React.FC = () => {
    const { user } = useAuth();
    // Read-only insights accounts get coarser search cells with no search text.
    const isInsights = user?.role === 'insights';
    const today = useMemo(() => new Date(), []);
    const [city, setCity] = useState(DEFAULT_CITY);
    const [action, setAction] = useState<ActionHeatmapAction>(DEFAULT_ACTION);
    const [displayMode, setDisplayMode] = useState<ActionHeatmapDisplayMode>(DEFAULT_DISPLAY_MODE);
    const [startDate, setStartDate] = useState(() => formatDateInputValue(shiftDateByDays(new Date(), -30)));
    const [endDate, setEndDate] = useState(() => formatDateInputValue(new Date()));
    const [selectedItem, setSelectedItem] = useState<SelectedHeatmapItem | null>(null);
    const [selectedLotDetails, setSelectedLotDetails] = useState<ParkingLot | null>(null);
    const [isLotDetailsLoading, setIsLotDetailsLoading] = useState(false);
    const [lotDetailsError, setLotDetailsError] = useState<string | null>(null);

    const {
        cities,
        isLoading: isCitiesLoading,
        error: citiesError,
        mutate: mutateCities
    } = useCities();
    const {
        rawMapItems,
        isLoading: isMapLoading,
        error: mapError,
        mutate: mutateMap
    } = useParkingLotsMapView(city);
    const {
        data: heatmapData,
        isLoading: isHeatmapLoading,
        error: heatmapError,
        mutate: mutateHeatmap
    } = useActionHeatmapData(action, city, startDate, endDate);

    const parkingLotsForMap = useMemo(
        () => rawMapItems.map(normalizeMapItemToParkingLot) as ParkingLot[],
        [rawMapItems]
    );

    const lotById = useMemo(() => {
        const nextMap = new Map<string, ParkingLot>();
        parkingLotsForMap.forEach((lot) => {
            nextMap.set(lot.lotId, lot);
        });
        return nextMap;
    }, [parkingLotsForMap]);

    const countsByLotId = useMemo(() => {
        const nextMap = new Map<string, number>();
        heatmapData?.lotCounts.forEach((lotCount) => {
            nextMap.set(lotCount.lotId, lotCount.count);
        });
        return nextMap;
    }, [heatmapData]);

    const isSearchAction = action === 'search_performed';
    const isFavoritesCurrentAction = action === 'favorites_current';
    const selectedActionMeta = ACTION_OPTIONS.find((option) => option.value === action) ?? ACTION_OPTIONS[0];
    const selectedMapPlace = useMemo(() => {
        if (selectedItem?.type !== 'lot') {
            return null;
        }
        return lotById.get(selectedItem.lotId) ?? null;
    }, [lotById, selectedItem]);

    const polygonStylesByLotId = useMemo(() => {
        const styles: Record<string, Partial<google.maps.PolygonOptions>> = {};

        parkingLotsForMap.forEach((lot) => {
            const count = isSearchAction ? 0 : countsByLotId.get(lot.lotId) ?? 0;
            styles[lot.lotId] = getPolygonStyleForCount(count, heatmapData?.summary.maxCount ?? 0);
        });

        return styles;
    }, [countsByLotId, heatmapData?.summary.maxCount, isSearchAction, parkingLotsForMap]);

    const handleLotSelect = useCallback((lot: ParkingLot) => {
        setSelectedItem({
            type: 'lot',
            lotId: lot.lotId,
            count: countsByLotId.get(lot.lotId) ?? 0
        });
    }, [countsByLotId]);

    const countLabelMarkers = useMemo<ParkingLotMapOverlayMarker[]>(() => {
        if (isSearchAction || displayMode !== 'count') {
            return [];
        }

        return parkingLotsForMap.flatMap((lot) => {
            const count = countsByLotId.get(lot.lotId) ?? 0;
            if (count <= 0) {
                return [];
            }

            const position = getGeometryLabelPosition(lot.geometry);
            if (!position) {
                return [];
            }

            return [{
                id: `count-${lot.lotId}`,
                position,
                label: formatMarkerCount(count),
                title: `${lot.lotId}: ${count.toLocaleString()} ${selectedActionMeta.label.toLowerCase()}`,
                kind: 'count',
                scale: count > 99 ? 20 : 17,
                onClick: () => handleLotSelect(lot)
            }];
        });
    }, [countsByLotId, displayMode, handleLotSelect, isSearchAction, parkingLotsForMap, selectedActionMeta.label]);

    const searchMarkers = useMemo<ParkingLotMapOverlayMarker[]>(() => {
        if (!isSearchAction || !heatmapData) {
            return [];
        }

        return heatmapData.searchBuckets.map((bucket) => {
            const bucketId = `${bucket.lat}-${bucket.lng}`;
            return {
                id: `search-${bucketId}`,
                position: {
                    lat: bucket.lat,
                    lng: bucket.lng
                },
                label: formatMarkerCount(bucket.count),
                title: bucket.topQuery ? `${bucket.topQuery} (${bucket.count.toLocaleString()})` : `${bucket.count.toLocaleString()} searches`,
                kind: 'search',
                scale: getSearchMarkerScale(bucket.count, heatmapData.summary.maxCount),
                onClick: () => {
                    setSelectedItem({
                        type: 'search',
                        bucketId,
                        bucket
                    });
                }
            };
        });
    }, [heatmapData, isSearchAction]);

    const overlayMarkers = isSearchAction ? searchMarkers : countLabelMarkers;

    useEffect(() => {
        setSelectedItem(null);
        setSelectedLotDetails(null);
        setLotDetailsError(null);
    }, [action, city, displayMode, endDate, startDate]);

    useEffect(() => {
        if (selectedItem?.type !== 'lot') {
            setSelectedLotDetails(null);
            setIsLotDetailsLoading(false);
            setLotDetailsError(null);
            return;
        }

        let isCancelled = false;

        const loadLotDetails = async () => {
            setIsLotDetailsLoading(true);
            setLotDetailsError(null);

            try {
                const details = await getParkingLotById(selectedItem.lotId);
                if (!isCancelled) {
                    setSelectedLotDetails(details);
                }
            } catch (error) {
                if (!isCancelled) {
                    setLotDetailsError(error instanceof Error ? error.message : 'Failed to load lot details');
                    setSelectedLotDetails(null);
                }
            } finally {
                if (!isCancelled) {
                    setIsLotDetailsLoading(false);
                }
            }
        };

        loadLotDetails();

        return () => {
            isCancelled = true;
        };
    }, [selectedItem]);

    if (citiesError || mapError || heatmapError) {
        return (
            <ErrorState
                error={citiesError?.message || mapError?.message || heatmapError?.message || 'Failed to load heatmap'}
                onRetry={() => {
                    mutateCities();
                    mutateMap();
                    mutateHeatmap();
                }}
            />
        );
    }

    if (isCitiesLoading || (isMapLoading && parkingLotsForMap.length === 0) || (isHeatmapLoading && !heatmapData)) {
        return <LoadingState message="Loading action heatmap..." />;
    }

    return (
        <div className="space-y-4">
            <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Action Heatmap</h1>
                    <p className="text-gray-600">
                        Compare where users perform key actions across parking lots and search locations.
                    </p>
                </div>
                <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900 md:max-w-sm">
                    <div className="flex items-start gap-2">
                        <Info className="mt-0.5 h-4 w-4 flex-shrink-0" />
                        <span>{selectedActionMeta.description}</span>
                    </div>
                </div>
            </div>

            <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
                    <label className="flex flex-col gap-2 text-sm font-medium text-gray-700">
                        City
                        <select
                            value={city}
                            onChange={(event) => setCity(event.target.value)}
                            className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                        >
                            <option value="all">All cities</option>
                            {cities.map((availableCity) => (
                                <option key={availableCity.id} value={availableCity.id}>
                                    {availableCity.name}
                                </option>
                            ))}
                        </select>
                    </label>

                    <label className="flex flex-col gap-2 text-sm font-medium text-gray-700">
                        Action
                        <select
                            value={action}
                            onChange={(event) => setAction(event.target.value as ActionHeatmapAction)}
                            className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                        >
                            {ACTION_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </label>

                    <label className="flex flex-col gap-2 text-sm font-medium text-gray-700">
                        Start date
                        <div className="relative">
                            <Calendar className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                            <input
                                type="date"
                                value={startDate}
                                max={endDate}
                                disabled={isFavoritesCurrentAction}
                                onChange={(event) => setStartDate(event.target.value)}
                                className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500"
                            />
                        </div>
                        {isFavoritesCurrentAction && (
                            <span className="text-xs font-normal text-gray-500">
                                Favorites uses the current saved snapshot, so date filters are disabled.
                            </span>
                        )}
                    </label>

                    <label className="flex flex-col gap-2 text-sm font-medium text-gray-700">
                        End date
                        <div className="relative">
                            <Calendar className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                            <input
                                type="date"
                                value={endDate}
                                min={startDate}
                                max={formatDateInputValue(today)}
                                disabled={isFavoritesCurrentAction}
                                onChange={(event) => setEndDate(event.target.value)}
                                className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500"
                            />
                        </div>
                        {isFavoritesCurrentAction && (
                            <span className="text-xs font-normal text-gray-500">
                                Snapshot actions are not filtered by a time window.
                            </span>
                        )}
                    </label>

                    <label className="flex flex-col gap-2 text-sm font-medium text-gray-700">
                        Display mode
                        <select
                            value={displayMode}
                            disabled={isSearchAction}
                            onChange={(event) => setDisplayMode(event.target.value as ActionHeatmapDisplayMode)}
                            className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500"
                        >
                            {DISPLAY_MODE_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                        {isSearchAction && (
                            <span className="text-xs font-normal text-gray-500">
                                Search events always use markers, so polygon display mode is disabled.
                            </span>
                        )}
                    </label>
                </div>
            </div>

            {heatmapData && (
                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                    {summaryCards.map((card) => {
                        const Icon = card.icon;
                        const value = heatmapData.summary[card.key];

                        return (
                            <div key={card.key} className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm font-medium text-gray-600">{card.label}</span>
                                    <Icon className="h-4 w-4 text-gray-400" />
                                </div>
                                <div className="mt-3 text-2xl font-semibold text-gray-900">
                                    {value.toLocaleString()}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_340px]">
                <div className="relative h-[70vh] min-h-[560px] overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
                    {heatmapData?.summary.totalMatchedEvents === 0 && (
                        <div className="absolute left-4 top-4 z-10 max-w-sm rounded-xl border border-amber-200 bg-white/95 px-4 py-3 text-sm text-amber-900 shadow-sm backdrop-blur">
                            No matching events were found for the selected filters. The map is still showing the available lot polygons for context.
                        </div>
                    )}

                    <ParkingLotMap
                        parkingLots={parkingLotsForMap}
                        selectedPlace={selectedMapPlace}
                        editMode={false}
                        creatingNew={false}
                        currentCoordinates={[]}
                        polygonStylesByLotId={polygonStylesByLotId}
                        overlayMarkers={overlayMarkers}
                        onPlaceSelect={handleLotSelect}
                    />
                </div>

                <aside className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                    <div className="flex items-center gap-2">
                        <MapIcon className="h-5 w-5 text-blue-600" />
                        <h2 className="text-lg font-semibold text-gray-900">Selection details</h2>
                    </div>

                    {!selectedItem && (
                        <div className="mt-6 rounded-xl border border-dashed border-gray-300 bg-gray-50 p-5 text-sm text-gray-600">
                            Click a polygon or marker to inspect the exact count for that location.
                        </div>
                    )}

                    {selectedItem?.type === 'lot' && (
                        <div className="mt-5 space-y-4">
                            <div className="rounded-xl bg-blue-50 p-4">
                                <div className="text-sm font-medium text-blue-700">Selected lot</div>
                                <div className="mt-1 text-xl font-semibold text-blue-950">
                                    {selectedLotDetails?.name || selectedLotDetails?.address || selectedItem.lotId}
                                </div>
                                <div className="mt-2 text-sm text-blue-900">
                                    Lot ID: <span className="font-mono">{selectedItem.lotId}</span>
                                </div>
                            </div>

                            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-1">
                                <div className="rounded-xl border border-gray-200 p-4">
                                    <div className="text-sm text-gray-500">Action count</div>
                                    <div className="mt-1 text-2xl font-semibold text-gray-900">
                                        {selectedItem.count.toLocaleString()}
                                    </div>
                                    <div className="mt-1 text-sm text-gray-600">{selectedActionMeta.label}</div>
                                </div>
                                <div className="rounded-xl border border-gray-200 p-4">
                                    <div className="text-sm text-gray-500">Parking type</div>
                                    <div className="mt-1 text-base font-medium text-gray-900">
                                        {selectedLotDetails?.parking_type || selectedMapPlace?.parking_type || 'Unknown'}
                                    </div>
                                    {(selectedLotDetails?.address || selectedLotDetails?.lotId) && (
                                        <div className="mt-2 text-sm text-gray-600">
                                            {selectedLotDetails?.address || selectedLotDetails?.lotId}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {isLotDetailsLoading && (
                                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
                                    Loading lot details...
                                </div>
                            )}

                            {lotDetailsError && (
                                <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                                    {lotDetailsError}
                                </div>
                            )}
                        </div>
                    )}

                    {selectedItem?.type === 'search' && (
                        <div className="mt-5 space-y-4">
                            <div className="rounded-xl bg-blue-50 p-4">
                                <div className="flex items-center gap-2 text-sm font-medium text-blue-700">
                                    <Search className="h-4 w-4" />
                                    Search marker
                                </div>
                                <div className="mt-2 text-3xl font-semibold text-blue-950">
                                    {selectedItem.bucket.count.toLocaleString()}
                                </div>
                                <div className="text-sm text-blue-900">searches in this bucket</div>
                            </div>

                            {!isInsights && (
                                <div className="rounded-xl border border-gray-200 p-4">
                                    <div className="text-sm text-gray-500">Top query</div>
                                    <div className="mt-1 text-base font-medium text-gray-900">
                                        {selectedItem.bucket.topQuery || 'No query label available'}
                                    </div>
                                </div>
                            )}

                            <div className="rounded-xl border border-gray-200 p-4">
                                <div className="text-sm text-gray-500">Coordinates</div>
                                <div className="mt-2 flex items-center gap-2 text-sm text-gray-900">
                                    <MapPin className="h-4 w-4 text-gray-400" />
                                    <span>
                                        {selectedItem.bucket.lat.toFixed(4)}, {selectedItem.bucket.lng.toFixed(4)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}

                    {heatmapData && (
                        <div className="mt-6 rounded-xl border border-gray-200 bg-gray-50 p-4">
                            <div className="text-sm font-medium text-gray-700">Active filter summary</div>
                            <div className="mt-3 space-y-2 text-sm text-gray-600">
                                <div>Action: {selectedActionMeta.label}</div>
                                <div>
                                    Date range: {isFavoritesCurrentAction ? 'Not applied (current favorites snapshot)' : `${startDate} to ${endDate}`}
                                </div>
                                <div>
                                    Map mode: {isSearchAction ? 'Markers only' : displayMode === 'count' ? 'Count labels + intensity' : 'Heat intensity'}
                                </div>
                                <div>
                                    Search buckets: {heatmapData.summary.searchBuckets.toLocaleString()}
                                </div>
                                {isInsights && isSearchAction && (heatmapData.summary.suppressedCount ?? 0) > 0 && (
                                    <div>
                                        Searches withheld (fewer than 3 in a ~110 m area):{' '}
                                        {heatmapData.summary.suppressedCount!.toLocaleString()}
                                    </div>
                                )}
                                <div>
                                    Total visible activity: {formatCompactCount(heatmapData.summary.totalMatchedEvents)}
                                </div>
                            </div>
                        </div>
                    )}
                </aside>
            </div>
        </div>
    );
};

export default ActionHeatmapPage;
